<template>
  <div class="home" ref="homeContainer">
    <section class="hero parallax-container">
      <div class="hero-background">
        <div class="gradient-overlay"></div>
        <div class="pattern-overlay"></div>
        <div class="particles" ref="particles"></div>
      </div>
      <div class="hero-content">
        <div class="container">
          <div class="content-wrapper">
            <h1 class="animate-fade-up">
              <span class="highlight">九火互娱</span>
              <div class="tagline">数字化转型的引领者</div>
            </h1>
            <div class="description animate-fade-up">
              <div class="typing-text">科技赋能未来 • 创新驱动发展</div>
              <div class="sub-description">
                专注于为企业提供智能化解决方案
                <br />
                让数字科技为企业创造更大价值
              </div>
            </div>
            <div class="cta-buttons animate-fade-up">
              <router-link to="/services" class="btn primary">
                <span>了解更多</span>
                <i class="fas fa-arrow-right"></i>
              </router-link>
            </div>
          </div>
        </div>
      </div>
      <div class="scroll-indicator">
        <div class="mouse">
          <div class="wheel"></div>
        </div>
        <div class="arrow">
          <i class="fas fa-chevron-down"></i>
        </div>
      </div>
    </section>

    <section class="advantages-section">
      <div class="container">
        <h2 class="section-title">核心优势</h2>
        <p class="section-subtitle">
          以科技驱动企业成长，为您提供全方位数字化解决方案
        </p>

        <div class="advantages-grid">
          <div
            class="advantage-card"
            v-for="(advantage, index) in advantages"
            :key="index"
          >
            <div class="card-icon">
              <i :class="advantage.icon"></i>
            </div>
            <h3>{{ advantage.title }}</h3>
            <p>{{ advantage.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="products-section">
      <div class="container">
        <h2 class="section-title">产品服务</h2>
        <p class="section-subtitle">四大核心主题产品，助力企业数字化转型</p>

        <div class="products-showcase">
          <div
            class="product-card"
            v-for="(product, index) in products"
            :key="index"
          >
            <div class="product-icon">
              <i :class="product.icon"></i>
            </div>
            <h3>{{ product.title }}</h3>
            <p>{{ product.description }}</p>
            <div class="features-list">
              <span v-for="(feature, fIndex) in product.features" :key="fIndex">
                {{ feature }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- <section class="features-section">
      <div
        class="features-background parallax-element"
        ref="featuresBackground"
      ></div>
      <div class="container">
        <h2 class="section-title">
          <span class="title-decoration"></span>
          我们的优势
          <span class="title-decoration"></span>
        </h2>
        <div class="features-grid">
          <div
            class="feature-card"
            v-for="(feature, index) in features"
            :key="index"
            :class="{ appear: true }"
            :style="{ '--delay': `${index * 0.2}s` }"
          >
            <div class="feature-icon">
              <i :class="feature.icon"></i>
            </div>
            <div class="feature-content">
              <h3>{{ feature.title }}</h3>
              <p>{{ feature.description }}</p>
            </div>
            <div class="feature-decoration"></div>
          </div>
        </div>
      </div>
    </section> -->

    <section class="achievements" ref="achievementsSection">
      <div class="achievements-bg"></div>
      <div class="container">
        <h2 class="section-title">我们的成就</h2>
        <div class="achievements-grid">
          <div
            class="achievement-card"
            v-for="(stat, index) in stats"
            :key="index"
          >
            <div class="achievement-icon">
              <i :class="stat.icon"></i>
            </div>
            <div class="achievement-number" ref="achievementNumbers">
              {{ stat.current }}
              <span class="plus" v-if="stat.showPlus">+</span>
            </div>
            <div class="achievement-label">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <section class="ai-model-section">
      <div class="container">
        <h2 class="section-title">
          <span class="title-decoration"></span>
          智能服务
          <span class="title-decoration"></span>
        </h2>
        <div class="ai-model-wrapper">
          <AIChatBox :key="aiComponentVersion" />
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import AIChatBox from "@/components/AIChatBox.vue";

const homeContainer = ref(null);
const heroBackground = ref(null);
const heroContent = ref(null);
const particles = ref(null);
const achievementsSection = ref(null);
const achievementNumbers = ref([]);
const featuresBackground = ref(null);
const aboutSection = ref(null);

const features = ref([
  {
    icon: "fas fa-code",
    title: "全栈开发",
    description: "提供从前端到后端的完整技术解决方案",
  },
  {
    icon: "fas fa-gamepad",
    title: "游戏运营",
    description: "专业的游戏运营团队，深度把控产品生命周期",
  },
  {
    icon: "fas fa-users",
    title: "精英团队",
    description: "来自互联网大厂的资深开发团队",
  },
]);

const stats = ref([
  {
    number: 20,
    current: 0,
    label: "成功项目",
    icon: "fas fa-trophy",
    showPlus: true,
    animated: false,
  },
  {
    number: 20,
    current: 0,
    label: "企业客户",
    icon: "fas fa-building",
    showPlus: true,
    animated: false,
  },
  {
    number: 98,
    current: 0,
    label: "客户满意度",
    icon: "fas fa-smile",
    showPlus: false,
    animated: false,
  },
  {
    number: 24,
    current: 0,
    label: "小时服务",
    icon: "fas fa-clock",
    showPlus: false,
    animated: false,
  },
]);

const throttledCheckScroll = ref(null);

const handle3DEffect = (e) => {
  if (heroBackground.value && heroContent.value) {
    const rect = heroBackground.value.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = (y - centerY) / 20;
    const rotateY = (centerX - x) / 20;

    heroContent.value.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
  }
};

const handleCardHover = (e) => {
  const card = e.currentTarget;
  const rect = card.getBoundingClientRect();
  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  const shine = card.querySelector(".card-shine");
  shine.style.background = `radial-gradient(circle at ${x}px ${y}px, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 80%)`;
};

const handleCardLeave = (e) => {
  const card = e.currentTarget;
  const shine = card.querySelector(".card-shine");
  shine.style.background = "none";
};

const throttle = (fn, delay) => {
  let last = 0;
  return (...args) => {
    const now = Date.now();
    if (now - last >= delay) {
      last = now;
      fn.apply(this, args);
    }
  };
};

const checkScroll = () => {
  requestAnimationFrame(() => {
    if (!achievementsSection.value) return;

    const rect = achievementsSection.value.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight && rect.bottom >= 0;

    if (isVisible) {
      stats.value.forEach((stat) => {
        if (!stat.animated) {
          animateNumber(stat);
        }
      });
    }
  });
};

const animateNumber = (stat) => {
  if (stat.animated) return;

  stat.animated = true;
  const startTime = performance.now();
  const duration = 1500; // 减少动画时间以提高性能

  const animate = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // 使用 easeOutQuart 缓动函数使动画更流畅
    const easeProgress = 1 - Math.pow(1 - progress, 4);
    stat.current = Math.round(stat.number * easeProgress);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else {
      stat.current = stat.number;
    }
  };

  requestAnimationFrame(animate);
};

const handleParallax = () => {
  requestAnimationFrame(() => {
    const scrolled = window.pageYOffset;

    // Hero 背景视差
    if (heroBackground.value) {
      heroBackground.value.style.transform = `translateY(${scrolled * 0.5}px)`;
    }

    // 特性背景视差
    if (featuresBackground.value) {
      const featureRect = featuresBackground.value.getBoundingClientRect();
      const featureOffset = featureRect.top + scrolled;
      const parallaxOffset = (scrolled - featureOffset) * 0.4;
      featuresBackground.value.style.transform = `translateY(${parallaxOffset}px)`;
    }
  });
};

onMounted(() => {
  throttledCheckScroll.value = throttle(checkScroll, 100);
  window.addEventListener("scroll", throttledCheckScroll.value, {
    passive: true,
  });
  checkScroll();

  const particlesConfig = {
    particles: {
      number: { value: 80, density: { enable: true, value_area: 800 } },
      color: { value: "#ffffff" },
      shape: { type: "circle" },
      opacity: { value: 0.5, random: true },
      size: { value: 3, random: true },
      line_linked: {
        enable: true,
        distance: 150,
        color: "#ffffff",
        opacity: 0.4,
        width: 1,
      },
      move: {
        enable: true,
        speed: 2,
        direction: "none",
        random: true,
        straight: false,
        out_mode: "out",
        bounce: false,
      },
    },
    interactivity: {
      detect_on: "canvas",
      events: {
        onhover: { enable: true, mode: "grab" },
        onclick: { enable: true, mode: "push" },
        resize: true,
      },
    },
    retina_detect: true,
  };

  if (typeof window !== "undefined" && window.particlesJS) {
    window.particlesJS("particles", particlesConfig);
  }

  window.addEventListener("scroll", handleParallax, { passive: true });
});

onUnmounted(() => {
  if (throttledCheckScroll.value) {
    window.removeEventListener("scroll", throttledCheckScroll.value);
  }
  window.removeEventListener("scroll", handleParallax);
});

// 通过 key 强制组件重载以保证最新代码生效
const aiComponentVersion = ref(Date.now());

// 添加公司价值观数据
const companyValues = [
  {
    icon: "fas fa-rocket",
    title: "创新驱动",
    description: "持续创新，引领技术发展，为客户带来更多可能",
  },
  {
    icon: "fas fa-handshake",
    title: "诚信为本",
    description: "诚信经营，信守承诺，与客户共同成长",
  },
  {
    icon: "fas fa-users",
    title: "专业团队",
    description: "技术精湛，经验丰富，打造精品服务",
  },
];

// 添加公司统计数据
const companyStats = [
  {
    icon: "fas fa-project-diagram",
    title: "项目经验",
    number: "100",
    suffix: "+",
    description: "成功案例",
  },
  {
    icon: "fas fa-user-friends",
    title: "服务客户",
    number: "50",
    suffix: "+",
    description: "长期合作",
  },
  {
    icon: "fas fa-code-branch",
    title: "技术积累",
    number: "5",
    suffix: "年+",
    description: "行业经验",
  },
];

const advantages = [
  {
    icon: "fas fa-chart-line",
    title: "先进数字化运营",
    description: "提供完整的数字化运营解决方案，助力企业实现精细化管理",
  },
  {
    icon: "fas fa-bullseye",
    title: "数字精准营销",
    description: "基于大数据分析，实现精准用户画像，提供个性化营销方案",
  },
  {
    icon: "fas fa-users",
    title: "深度客户洞察",
    description: "多维度分析客户行为，助力企业提升客户满意度",
  },
  {
    icon: "fas fa-mobile-alt",
    title: "品质用户体验",
    description: "专注用户体验设计，打造流畅便捷的操作界面",
  },
  {
    icon: "fas fa-robot",
    title: "智能成本控制",
    description: "通过智能化手段，帮助企业实现成本精细化管理",
  },
  {
    icon: "fas fa-piggy-bank",
    title: "超高性价比",
    description: "手续费低于市场均价，确保最优投资回报比",
  },
];

const products = [
  {
    icon: "fas fa-gamepad",
    title: "游戏运营解决方案",
    description: "专业的游戏运营体系，助力游戏成功",
    features: ["用户运营", "活动策划", "数据分析", "付费转化", "社区运营"],
  },
  {
    icon: "fas fa-store",
    title: "门店运营系统",
    description: "全方位提升门店运营效率",
    features: ["POS系统", "会员管理", "库存管理", "数据分析"],
  },
  {
    icon: "fas fa-shopping-cart",
    title: "新零售解决方案",
    description: "线上线下一体化运营",
    features: ["线上商城", "订单管理", "配送系统", "营销工具"],
  },
  {
    icon: "fas fa-user-check",
    title: "会员管理系统",
    description: "提升客户忠诚度",
    features: ["会员积分", "等级权益", "精准营销", "数据分析"],
  },
  {
    icon: "fas fa-chart-bar",
    title: "数据分析平台",
    description: "助力企业决策优化",
    features: ["实时监控", "趋势分析", "预测模型", "报表导出"],
  },
  {
    icon: "fas fa-handshake-angle",
    title: "解决方案授权",
    description: "提供成熟的技术解决方案授权服务，助力合作伙伴快速构建业务生态",
    features: [
      "SaaS系统租赁",
      "技术方案授权",
      "定制化开发",
      "运维托管",
      "商务支持",
    ],
  },
];

const services = [
  // ... 前面5个服务保持不变
  {
    icon: "fas fa-handshake-angle",
    title: "解决方案授权",
    description: "提供成熟的技术解决方案授权服务，助力合作伙伴快速构建业务生态",
    details: [
      {
        title: "SaaS系统租赁",
        text: "提供即用型解决方案，支持按需部署，灵活的商业模式",
      },
      {
        title: "技术方案授权",
        text: "成熟系统授权，包含源码交付、部署支持、技术培训等",
      },
      {
        title: "定制化服务",
        text: "基于现有系统进行个性化定制，快速满足市场需求",
      },
    ],
    color: "linear-gradient(135deg, #3498db, #2980b9)",
    shadowColor: "rgba(52, 152, 219, 0.15)",
    link: "/services#solutions",
  },
];
</script>

<style scoped>
:root {
  --primary-color: #2c3e50;
  --secondary-color: #3498db;
  --accent-color: #e74c3c;
  --bg-light: #f8f9fa;
  --bg-dark: #2c3e50;
  --text-light: #ecf0f1;
  --text-dark: #2c3e50;
}

.animate-fade-up {
  opacity: 0;
  transform: translateY(30px);
  animation: fadeUp 0.8s ease forwards;
}

.animate-on-scroll {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s ease;
}

.animate-on-scroll.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.hero {
  position: relative;
  height: 100vh;
  min-height: 700px;
  overflow: hidden;
  display: flex;
  align-items: center;
  background-image: url(https://images.unsplash.com/photo-1550751827-4bd374c3f58b?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80);
  background-size: cover;
  background-position: center;
}

.hero::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.9) 0%,
    rgba(0, 0, 0, 0.7) 50%,
    rgba(245, 187, 68, 0.5) 100%
  );
}

.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  will-change: transform;
  transform: translateZ(0);
}

.gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
      circle at 20% 30%,
      rgba(245, 187, 68, 0.15) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 70%,
      rgba(245, 187, 68, 0.15) 0%,
      transparent 50%
    );
  opacity: 0.8;
  animation: pulseGlow 8s ease-in-out infinite;
}

.pattern-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: linear-gradient(
      rgba(245, 187, 68, 0.1) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(245, 187, 68, 0.1) 1px, transparent 1px);
  background-size: 30px 30px;
  opacity: 0.3;
  animation: patternMove 20s linear infinite;
}

.particles {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
}

.hero-content {
  position: relative;
  z-index: 4;
  width: 100%;
  padding: 0 20px;
}

.content-wrapper {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  animation: float 6s ease-in-out infinite;
}

.highlight {
  font-size: 4.5rem;
  font-weight: bold;
  background: linear-gradient(135deg, #ffffff 0%, #f5bb44 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: block;
  margin-bottom: 1rem;
  text-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  letter-spacing: 2px;
}

.tagline {
  font-size: 2.4rem;
  color: #f5bb44;
  font-weight: 600;
  margin: 1.5rem 0;
  letter-spacing: 3px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
}

.description {
  margin: 2rem 0;
}

.typing-text {
  font-size: 1.8rem;
  color: white;
  margin-bottom: 1.5rem;
  font-weight: 500;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  position: relative;
  display: inline-block;
}

.typing-text::after {
  content: "|";
  /* animation: blink 1s infinite; */
  opacity: 0;
  color: #f5bb44;
}

.sub-description {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.8;
  margin-top: 1rem;
}

.cta-buttons {
  margin-top: 3rem;
}

.btn.primary {
  background: linear-gradient(135deg, #f5bb44 0%, #f4a834 100%);
  border: none;
  padding: 18px 45px;
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: 1px;
  border-radius: 30px;
  box-shadow: 0 5px 20px rgba(245, 187, 68, 0.3);
  transition: all 0.3s ease;
}

.btn.primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(245, 187, 68, 0.5);
}

.btn.primary i {
  transition: transform 0.3s ease;
}

.btn.primary:hover i {
  transform: translateX(5px);
}

@keyframes blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

@media (max-width: 768px) {
  .highlight {
    font-size: 3.2rem;
  }

  .tagline {
    font-size: 1.8rem;
  }

  .pattern-overlay {
    background-size: 20px 20px;
  }
}

.scroll-indicator {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: white;
  z-index: 3;
}

.mouse {
  width: 30px;
  height: 50px;
  border: 2px solid white;
  border-radius: 15px;
  position: relative;
}

.wheel {
  width: 4px;
  height: 8px;
  background: white;
  border-radius: 2px;
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  animation: scroll 1.5s infinite;
}

.arrow {
  animation: bounce 2s infinite;
  opacity: 0.7;
}

@keyframes scroll {
  0% {
    transform: translate(-50%, 0);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, 20px);
    opacity: 0;
  }
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

/* 响应式调整 */
@media (max-width: 768px) {
  .hero-background {
    background-attachment: scroll;
  }

  .pattern-overlay {
    opacity: 0.3;
  }

  .hero::after {
    animation: none;
  }
}

@media (max-width: 480px) {
  .cta-buttons {
    flex-direction: column;
    gap: 15px;
  }

  .btn {
    width: 100%;
    justify-content: center;
  }
}

.parallax-container {
  position: relative;
  overflow: hidden;
}

.parallax-element {
  will-change: transform;
}

.features-section {
  position: relative;
  padding: 120px 0;
  background: #f8f9fa;
  overflow: hidden;
}

.features-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(52, 152, 219, 0.05) 0%,
    rgba(41, 128, 185, 0.1) 100%
  );
  z-index: 1;
}

.section-title {
  position: relative;
  text-align: center;
  font-size: 2.5rem;
  color: #2c3e50;
  margin-bottom: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
}

.title-decoration {
  display: inline-block;
  width: 40px;
  height: 3px;
  background: var(--primary-color);
  margin: 0 15px;
  vertical-align: middle;
  position: relative;
}

.section-subtitle {
  text-align: center;
  color: var(--text-secondary);
  font-size: 1.1rem;
  margin-bottom: 3rem;
}

.features-grid {
  position: relative;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  padding: 20px;
}

.feature-card {
  background: white;
  border-radius: 20px;
  padding: 40px 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  opacity: 0;
  transform: translateY(30px);
}

.feature-card.appear {
  animation: cardAppear 0.6s ease forwards;
  animation-delay: var(--delay);
}

.feature-icon {
  font-size: 2.5rem;
  color: var(--primary-color);
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
}

.feature-icon i {
  background: linear-gradient(
    135deg,
    var(--primary-color),
    var(--secondary-color)
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.feature-content h3 {
  font-size: 1.5rem;
  color: #2c3e50;
  margin-bottom: 15px;
}

.feature-content p {
  color: #666;
  line-height: 1.6;
}

.feature-decoration {
  position: absolute;
  top: 0;
  right: 0;
  width: 100px;
  height: 100px;
  background: linear-gradient(
    135deg,
    rgba(52, 152, 219, 0.1),
    rgba(41, 128, 185, 0.05)
  );
  border-radius: 0 20px 0 100%;
  z-index: 0;
}

.feature-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
}

.feature-card:hover .feature-icon i {
  animation: iconPulse 1s ease infinite;
}

@keyframes cardAppear {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes iconPulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

/* 响应式调整 */
@media (max-width: 768px) {
  .features-section {
    padding: 80px 0;
  }

  .section-title {
    font-size: 2rem;
    margin-bottom: 60px;
  }

  .features-grid {
    gap: 30px;
  }

  .feature-card {
    padding: 30px 20px;
  }
}

.services {
  padding: 100px 0;
  background: #f9f9f9;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.service-item {
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.service-image {
  position: relative;
  height: 250px;
  overflow: hidden;
}

.service-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.service-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.2),
    rgba(0, 0, 0, 0.6)
  );
}

.service-item:hover .service-image img {
  transform: scale(1.1);
}

.service-content {
  padding: 30px;
}

.service-content ul {
  list-style: none;
  padding: 0;
  margin-top: 15px;
}

.service-content li {
  margin: 10px 0;
  padding-left: 20px;
  position: relative;
}

.service-content li:before {
  content: "•";
  color: var(--primary-color);
  position: absolute;
  left: 0;
}

.cases {
  padding: 100px 0;
}

.case-slider {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.case-card {
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
}

.case-image {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.case-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.case-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.2),
    rgba(0, 0, 0, 0.6)
  );
  opacity: 0;
  transition: opacity 0.3s;
}

.case-card:hover .case-image img {
  transform: scale(1.1);
}

.case-card:hover .case-overlay {
  opacity: 1;
}

.case-info {
  padding: 20px;
}

.contact-section {
  padding: 100px 0;
  background: #f9f9f9;
}

.contact-grid {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 50px;
}

.contact-info {
  padding: 30px;
}

.info-item {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.info-item i {
  font-size: 24px;
  color: var(--primary-color);
  margin-right: 15px;
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.contact-form input,
.contact-form textarea {
  padding: 15px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 16px;
}

.contact-form textarea {
  height: 150px;
  resize: vertical;
}

.particles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.service-item {
  transition: all 0.3s ease;
}

.service-item:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.2);
}

.service-image::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    45deg,
    var(--primary-color),
    var(--secondary-color)
  );
  opacity: 0;
  transition: opacity 0.3s ease;
}

.service-item:hover .service-image::after {
  opacity: 0.3;
}

.case-card {
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.case-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: 0.5s;
}

.case-card:hover::before {
  left: 100%;
}

.case-info {
  transform: translateY(100%);
  transition: transform 0.3s ease;
  background: rgba(44, 62, 80, 0.9);
  padding: 20px;
  position: absolute;
  bottom: 0;
  width: 100%;
}

.case-card:hover .case-info {
  transform: translateY(0);
}

.btn {
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.btn::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: width 0.6s ease, height 0.6s ease;
}

.btn:hover::after {
  width: 300px;
  height: 300px;
}

.feature-card {
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
}

.feature-card i {
  transition: transform 0.3s ease;
}

.feature-card:hover i {
  transform: scale(1.2) rotate(360deg);
}

.feature-card::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background: var(--accent-color);
  transform: scaleX(0);
  transition: transform 0.3s ease;
}

.feature-card:hover::after {
  transform: scaleX(1);
}

@keyframes fadeUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

@media (max-width: 768px) {
  .feature-grid,
  .services-grid {
    grid-template-columns: 1fr;
  }

  .case-info {
    transform: translateY(0);
  }
}

.map-section {
  position: relative;
  height: 400px;
  overflow: hidden;
}

.map-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.map-image {
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.location-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: grayscale(20%) saturate(90%);
  transition: transform 0.3s ease;
}

.map-container:hover .location-image {
  transform: scale(1.05);
}

.map-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(44, 62, 80, 0.2),
    rgba(52, 152, 219, 0.2)
  );
  pointer-events: none;
}

.pulse-point {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.pulse-circle {
  position: absolute;
  width: 20px;
  height: 20px;
  background: var(--accent-color);
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

.pulse-circle:nth-child(1) {
  animation: pulse 2s infinite;
}

.pulse-circle:nth-child(2) {
  animation: pulse 2s infinite 0.3s;
}

.pulse-circle:nth-child(3) {
  animation: pulse 2s infinite 0.6s;
}

.company-info {
  position: absolute;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.95);
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  text-align: center;
  animation: slideUp 0.8s ease;
  backdrop-filter: blur(5px);
}

.company-info h3 {
  color: var(--primary-color);
  margin-bottom: 10px;
}

.company-info p {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.company-info i {
  color: var(--accent-color);
}

@keyframes pulse {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(3);
    opacity: 0;
  }
}

@keyframes slideUp {
  from {
    transform: translate(-50%, 100%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, 0);
    opacity: 1;
  }
}

.map-container::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
    circle at var(--mouse-x, 50%) var(--mouse-y, 50%),
    rgba(52, 152, 219, 0.3) 0%,
    transparent 60%
  );
  opacity: 0;
  transition: opacity 0.3s;
  pointer-events: none;
  z-index: 1;
}

.map-container:hover::before {
  opacity: 1;
}

@media (max-width: 768px) {
  .map-section {
    height: 300px;
  }

  .company-info {
    width: 90%;
    padding: 15px;
  }
}

.hero-content {
  transition: transform 0.3s ease;
  transform-style: preserve-3d;
}

.advantage-card {
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.95);
  z-index: 1;
  text-align: center !important;
}

.card-shine {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  transition: background 0.3s ease;
}

.stat-number {
  display: inline-block;
  transition: transform 0.3s ease;
}

.stat-number:hover {
  transform: scale(1.1);
}

.advantage-card:hover .card-icon {
  transform: rotateY(360deg);
  transition: transform 1s ease;
}

@media (max-width: 768px) {
  .hero-content {
    transform: none !important;
  }
}

.achievements {
  position: relative;
  padding: 120px 0;
  color: white;
  overflow: hidden;
  background-image: url(https://images.unsplash.com/photo-1504384764586-bb4cdc1707b0?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80);
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
}

.achievements::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.9),
    rgba(245, 187, 68, 0.7)
  );
  z-index: 1;
}

.achievements .container {
  position: relative;
  z-index: 2;
}

.achievements .section-title {
  color: white;
  margin-bottom: 60px;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.achievements-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  padding: 20px;
}

.achievement-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 40px 30px;
  text-align: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
}

.achievement-card:hover {
  transform: translateY(-10px);
  background: rgba(255, 255, 255, 0.15);
  border-color: var(--primary-color);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.achievement-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: 0.5s;
}

.achievement-card:hover::before {
  left: 100%;
}

.achievement-icon {
  font-size: 40px;
  color: var(--primary-color);
  margin-bottom: 20px;
  position: relative;
  z-index: 1;
  text-shadow: 0 2px 10px rgba(245, 187, 68, 0.3);
}

.achievement-number {
  font-size: 3.5rem;
  font-weight: 700;
  color: white;
  line-height: 1.2;
  margin: 15px 0;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.achievement-label {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  letter-spacing: 1px;
  text-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.achievement-card {
  animation: fadeInUp 0.6s ease-out forwards;
  animation-delay: calc(var(--index) * 0.2s);
}

@media (max-width: 1200px) {
  .achievements-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .achievements {
    padding: 80px 0;
    background-attachment: scroll;
  }

  .achievements-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .achievement-card {
    padding: 30px 20px;
  }

  .achievement-number {
    font-size: 3rem;
  }

  .achievement-label {
    font-size: 1.1rem;
  }
}

@keyframes patternMove {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 30px 30px;
  }
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .pattern-overlay,
  .hero::after {
    animation: none;
  }
}

.ai-model-section {
  position: relative;
  padding: 120px 0;
  background: linear-gradient(135deg, #1a1f25 0%, #2c3e50 100%);
  overflow: hidden;
}

.ai-model-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(rgba(245, 187, 68, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(245, 187, 68, 0.05) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: patternMove 20s linear infinite;
}

.ai-model-section::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(
      circle at 20% 30%,
      rgba(245, 187, 68, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 70%,
      rgba(245, 187, 68, 0.1) 0%,
      transparent 50%
    );
  opacity: 0.5;
}

.ai-model-section .container {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 30px;
  position: relative;
  z-index: 2;
}

.ai-model-section .section-title {
  text-align: center;
  margin-bottom: 60px;
  font-size: 2.8rem;
  color: white;
  position: relative;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.title-decoration {
  display: inline-block;
  width: 50px;
  height: 3px;
  background: linear-gradient(90deg, transparent, #f5bb44, transparent);
  margin: 0 20px;
  vertical-align: middle;
  position: relative;
}

.title-decoration::before {
  content: "";
  position: absolute;
  width: 12px;
  height: 12px;
  background: #f5bb44;
  border-radius: 50%;
  top: 50%;
  transform: translateY(-50%);
  box-shadow: 0 0 15px rgba(245, 187, 68, 0.5);
}

.title-decoration:first-child::before {
  right: -6px;
}

.title-decoration:last-child::before {
  left: -6px;
}

.ai-model-wrapper {
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border-radius: 30px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3),
    inset 0 0 0 1px rgba(255, 255, 255, 0.1);
  padding: 40px;
  position: relative;
  overflow: hidden;
  transition: all 0.4s ease;
}

.ai-model-wrapper:hover {
  transform: translateY(-5px);
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.4),
    inset 0 0 0 1px rgba(245, 187, 68, 0.2);
  background: rgba(255, 255, 255, 0.03);
}

.ai-model-wrapper::before,
.ai-model-wrapper::after {
  content: "";
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(245, 187, 68, 0.1) 0%,
    transparent 60%
  );
  filter: blur(20px);
  opacity: 0;
  transition: opacity 0.4s ease;
}

.ai-model-wrapper:hover::before,
.ai-model-wrapper:hover::after {
  opacity: 1;
}

.ai-model-wrapper::before {
  top: -150px;
  left: -150px;
}

.ai-model-wrapper::after {
  bottom: -150px;
  right: -150px;
}

@media (max-width: 1440px) {
  .ai-model-section .container {
    max-width: 1200px;
  }
}

@media (max-width: 1200px) {
  .ai-model-section .container {
    max-width: 960px;
  }
}

@media (max-width: 768px) {
  .ai-model-section {
    padding: 80px 0;
  }

  .ai-model-section .section-title {
    font-size: 2.2rem;
  }

  .title-decoration {
    width: 40px;
    margin: 0 15px;
  }

  .ai-model-wrapper {
    padding: 30px 20px;
  }
}

.about-section {
  padding: 120px 0;
  background: linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%);
  position: relative;
  overflow: hidden;
}

.about-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(
      circle at 20% 30%,
      rgba(52, 152, 219, 0.05) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 70%,
      rgba(41, 128, 185, 0.05) 0%,
      transparent 50%
    );
  z-index: 0;
}

.section-header {
  text-align: center;
  margin-bottom: 80px;
}

.section-subtitle {
  font-size: 1.2rem;
  color: #666;
  margin-top: 15px;
  letter-spacing: 2px;
}

.about-content {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 80px;
}

.company-intro {
  background: rgba(255, 255, 255, 0.9);
  padding: 40px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  margin-bottom: 40px;
}

.intro-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 15px;
}

.company-badge {
  background: linear-gradient(135deg, #3498db, #2980b9);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
}

.company-intro h3 {
  font-size: 2.8rem;
  background: linear-gradient(135deg, #2c3e50, #3498db);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
}

.divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 30px 0;
}

.divider-line {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, #3498db, #2980b9);
  opacity: 0.3;
}

.divider-dot {
  width: 6px;
  height: 6px;
  background: #3498db;
  border-radius: 50%;
}

.value-item {
  position: relative;
  overflow: hidden;
  padding: 30px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  margin-bottom: 25px;
  border: 1px solid rgba(52, 152, 219, 0.1);
}

.value-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: rgba(52, 152, 219, 0.3);
}

.value-decoration {
  position: absolute;
  top: 0;
  right: 0;
  width: 150px;
  height: 150px;
  background: radial-gradient(
    circle at center,
    rgba(52, 152, 219, 0.05) 0%,
    transparent 70%
  );
  transform: translate(30%, -30%);
  pointer-events: none;
}

.stat-card {
  background: white;
  padding: 35px;
  border-radius: 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  margin-bottom: 30px;
  transition: all 0.3s ease;
  border: 1px solid rgba(52, 152, 219, 0.1);
}

.stat-card:hover {
  transform: translateX(10px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  border-color: rgba(52, 152, 219, 0.3);
}

.stat-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #3498db, #2980b9);
  border-radius: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  margin-bottom: 20px;
}

.stat-content h4 {
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 10px;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 10px;
  background: linear-gradient(135deg, #2c3e50, #3498db);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* 响应式调整 */
@media (max-width: 992px) {
  .about-content {
    grid-template-columns: 1fr;
    gap: 50px;
  }

  .company-intro {
    padding: 30px;
  }

  .value-item {
    padding: 25px;
  }

  .stat-card {
    padding: 30px;
  }
}

@media (max-width: 768px) {
  .about-section {
    padding: 80px 0;
  }

  .section-header {
    margin-bottom: 50px;
  }

  .company-intro {
    padding: 25px;
  }

  .value-item {
    margin-bottom: 20px;
  }

  .stat-card {
    padding: 25px;
    margin-bottom: 20px;
  }
}

.advantages-section,
.products-section {
  padding: 80px 0;
  background: #fff;
}

.section-title {
  text-align: center;
  font-size: 2.5rem;
  color: var(--text-primary);
  margin-bottom: 1rem;
}

.section-subtitle {
  text-align: center;
  color: var(--text-secondary);
  font-size: 1.1rem;
  margin-bottom: 3rem;
}

.advantages-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  padding: 20px;
}

.advantage-card {
  background: white;
  padding: 40px 30px;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(245, 187, 68, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(245, 187, 68, 0.1);
  position: relative;
  z-index: 1;
  overflow: hidden;
}

.advantage-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, var(--primary-color), var(--primary-dark));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.advantage-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(245, 187, 68, 0.15);
  border-color: rgba(245, 187, 68, 0.2);
}

.advantage-card:hover::before {
  opacity: 1;
}

.card-icon {
  width: 70px;
  height: 70px;
  margin: 0 auto 25px;
  background: linear-gradient(135deg, #fff9ef, white);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border: 2px solid rgba(245, 187, 68, 0.1);
}

.card-icon i {
  font-size: 28px;
  color: var(--primary-color);
  transition: all 0.3s ease;
}

.advantage-card:hover .card-icon {
  transform: rotateY(360deg);
  border-color: var(--primary-color);
}

.advantage-card h3 {
  color: var(--text-primary);
  font-size: 1.4rem;
  margin-bottom: 15px;
  font-weight: 600;
}

.advantage-card p {
  color: var(--text-secondary);
  line-height: 1.6;
  font-size: 1rem;
}

.advantages-grid {
  position: relative;
}

.advantages-grid::before,
.advantages-grid::after {
  content: "";
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(245, 187, 68, 0.1) 0%,
    transparent 70%
  );
  z-index: 0;
}

.advantages-grid::before {
  top: -100px;
  left: -100px;
}

.advantages-grid::after {
  bottom: -100px;
  right: -100px;
}

@media (max-width: 768px) {
  .advantages-section {
    padding: 60px 0;
  }

  .advantage-card {
    padding: 30px 20px;
  }

  .card-icon {
    width: 60px;
    height: 60px;
    margin-bottom: 20px;
  }

  .advantage-card h3 {
    font-size: 1.2rem;
  }
}

.products-showcase {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  padding: 20px;
}

/* 产品服务区域整体样式 */
.products-showcase {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  padding: 20px;
}

/* 产品卡片基础样式 */
.product-card {
  background: rgba(255, 255, 255, 0.98);
  padding: 40px;
  border-radius: 24px;
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.05);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(245, 187, 68, 0.1);
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* 图标样式 */
.product-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #fff9ef, #fff5e0);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 10px;
  position: relative;
  transition: all 0.5s ease;
}

.product-icon i {
  font-size: 28px;
  background: linear-gradient(135deg, #f5bb44, #f4a834);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 2px 4px rgba(245, 187, 68, 0.2));
}

/* 标题样式 */
.product-card h3 {
  font-size: 1.5rem;
  color: #2c3e50;
  margin: 15px 0;
  font-weight: 600;
  background: linear-gradient(135deg, #2c3e50, #34495e);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* 描述文本样式 */
.product-card p {
  color: #666;
  line-height: 1.6;
  margin-bottom: 20px;
  font-size: 1rem;
}

/* 特性列表样式 */
.features-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: auto;
}

.features-list span {
  padding: 8px 16px;
  background: rgba(245, 187, 68, 0.08);
  border-radius: 20px;
  font-size: 0.9rem;
  color: #f5bb44;
  transition: all 0.3s ease;
  border: 1px solid rgba(245, 187, 68, 0.1);
}

/* 悬浮效果 */
.product-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 40px rgba(245, 187, 68, 0.1);
  border-color: rgba(245, 187, 68, 0.2);
  background: rgba(255, 255, 255, 1);
}

.product-card:hover .product-icon {
  transform: scale(1.1) rotate(5deg);
  box-shadow: 0 8px 20px rgba(245, 187, 68, 0.15);
}

.product-card:hover .features-list span {
  background: rgba(245, 187, 68, 0.1);
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(245, 187, 68, 0.1);
}

/* 装饰效果 */
.product-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  background: linear-gradient(
    180deg,
    rgba(245, 187, 68, 0.05) 0%,
    transparent 100%
  );
  opacity: 0;
  transition: opacity 0.3s ease;
}

.product-card:hover::before {
  opacity: 1;
}

/* 添加光效 */
.product-card::after {
  content: "";
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(
    circle at center,
    rgba(245, 187, 68, 0.05) 0%,
    transparent 50%
  );
  opacity: 0;
  transition: opacity 0.3s ease;
  transform: translate(-50%, -50%) rotate(0deg);
  pointer-events: none;
}

.product-card:hover::after {
  opacity: 1;
  animation: rotateGradient 10s linear infinite;
}

@keyframes rotateGradient {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

/* 响应式调整 */
@media (max-width: 1200px) {
  .products-showcase {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .products-showcase {
    grid-template-columns: 1fr;
  }

  .product-card {
    padding: 30px;
  }
}
</style>
